import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 返回链接样式
const BackLink = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  color: '$textSecondary',
  fontSize: '0.9rem',
  textDecoration: 'none',
  marginBottom: '1rem',
  
  '&:hover': {
    color: '$accentPrimary'
  },
  
  '&::before': {
    content: '"←"',
    marginRight: '0.5rem',
    fontSize: '1.2rem'
  }
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 描述样式
const Description = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  marginBottom: '2rem',
  maxWidth: '800px'
});

// 内容区域容器样式
const ContentContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '2rem',
  
  '@media (min-width: 992px)': {
    gridTemplateColumns: '2fr 1fr'
  }
});

// 定制选项部分样式
const CustomizationSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 定制选项组样式
const OptionGroup = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
});

// 定制选项组标题样式
const OptionGroupTitle = styled('h2', {
  fontSize: '1.2rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginBottom: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  
  '&::before': {
    content: '""',
    display: 'inline-block',
    width: '12px',
    height: '12px',
    background: '$accentPrimary',
    borderRadius: '50%',
    marginRight: '0.5rem'
  }
});

// 定制选项组描述样式
const OptionGroupDescription = styled('p', {
  fontSize: '0.9rem',
  color: '$textSecondary',
  marginBottom: '1rem'
});

// 矿机选择区域样式
const MinerSelection = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '1rem'
});

// 矿机选项样式
const MinerOption = styled('div', {
  background: '$backgroundPrimary',
  borderRadius: '8px',
  border: '1px solid $borderColor',
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)'
  },
  
  variants: {
    selected: {
      true: {
        borderColor: '$accentPrimary',
        boxShadow: '0 0 0 2px rgba(6, 214, 160, 0.3)'
      }
    }
  }
});

// 矿机图片样式
const MinerImage = styled('div', {
  width: '100px',
  height: '100px',
  background: '$backgroundTertiary',
  borderRadius: '6px',
  marginBottom: '1rem'
});

// 矿机名称样式
const MinerName = styled('h3', {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginBottom: '0.25rem'
});

// 矿机算力样式
const MinerHashrate = styled('span', {
  fontSize: '0.9rem',
  color: '$textSecondary'
});

// 选择选项容器样式
const SelectionOptions = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 选项行样式
const OptionRow = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
});

// 选项标签样式
const OptionLabel = styled('label', {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: '$textSecondary'
});

// 选择框样式
const SelectInput = styled('select', {
  padding: '0.75rem',
  borderRadius: '6px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '0.9rem',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 数量输入容器样式
const QuantityInput = styled('div', {
  display: 'flex',
  alignItems: 'center',
  border: '1px solid $borderColor',
  borderRadius: '6px',
  overflow: 'hidden'
});

// 数量按钮样式
const QuantityButton = styled('button', {
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '$backgroundSecondary',
  border: 'none',
  fontSize: '1.2rem',
  color: '$textPrimary',
  cursor: 'pointer',
  
  '&:hover': {
    background: '$backgroundTertiary'
  },
  
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
});

// 数量显示样式
const QuantityDisplay = styled('input', {
  width: '60px',
  height: '40px',
  textAlign: 'center',
  border: 'none',
  borderLeft: '1px solid $borderColor',
  borderRight: '1px solid $borderColor',
  background: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '0.9rem',
  
  '&:focus': {
    outline: 'none'
  }
});

// 复选框容器样式
const CheckboxContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.75rem',
  background: '$backgroundPrimary',
  borderRadius: '6px',
  border: '1px solid $borderColor',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    borderColor: '$accentSecondary'
  },
  
  variants: {
    checked: {
      true: {
        borderColor: '$accentPrimary',
        background: 'rgba(6, 214, 160, 0.05)'
      }
    }
  }
});

// 复选框样式
const Checkbox = styled('div', {
  width: '20px',
  height: '20px',
  borderRadius: '4px',
  border: '2px solid $borderColor',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  
  variants: {
    checked: {
      true: {
        background: '$accentPrimary',
        borderColor: '$accentPrimary',
        
        '&::after': {
          content: '"✓"',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold'
        }
      }
    }
  }
});

// 复选框标签样式
const CheckboxLabel = styled('div', {
  display: 'flex',
  flexDirection: 'column'
});

// 复选框标题样式
const CheckboxTitle = styled('span', {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 复选框描述样式
const CheckboxDescription = styled('span', {
  fontSize: '0.8rem',
  color: '$textSecondary'
});

// 摘要区域样式
const SummarySection = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '1.5rem',
  position: 'sticky',
  top: '2rem',
  height: 'fit-content'
});

// 摘要标题样式
const SummaryTitle = styled('h2', {
  fontSize: '1.2rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginBottom: '1.5rem'
});

// 摘要项容器样式
const SummaryItemContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  marginBottom: '1.5rem'
});

// 摘要项样式
const SummaryItem = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.9rem',
  padding: '0.5rem 0',
  
  '&:not(:last-child)': {
    borderBottom: '1px solid $borderColor'
  }
});

// 摘要项标签样式
const SummaryItemLabel = styled('span', {
  color: '$textSecondary'
});

// 摘要项值样式
const SummaryItemValue = styled('span', {
  color: '$textPrimary',
  fontWeight: 'bold'
});

// 总价容器样式
const TotalContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 0',
  borderTop: '1px solid $borderColor',
  borderBottom: '1px solid $borderColor',
  marginBottom: '1.5rem'
});

// 总价标签样式
const TotalLabel = styled('span', {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 总价值样式
const TotalValue = styled('span', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$accentPrimary'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-2px)'
        }
      },
      secondary: {
        background: 'transparent',
        color: '$accentPrimary',
        border: '1px solid $accentPrimary',
        '&:hover': {
          background: 'rgba(6, 214, 160, 0.05)'
        }
      }
    },
    fullWidth: {
      true: {
        width: '100%'
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 交付选项样式
const DeliveryOption = styled('div', {
  marginBottom: '0.75rem'
});

/**
 * ASIC矿机定制页面组件
 * 允许用户自定义矿机配置和服务
 */
const AsicShopCustomization: React.FC = () => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  
  // 状态管理
  const [selectedMiner, setSelectedMiner] = useState<number | null>(1);
  const [quantity, setQuantity] = useState(1);
  const [powerSupply, setPowerSupply] = useState('standard');
  const [firmware, setFirmware] = useState('standard');
  const [options, setOptions] = useState({
    overclocking: false,
    customBranding: false,
    extendedWarranty: false,
    prioritySupport: false
  });
  
  // 处理数量变化
  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 100) {
      setQuantity(newQuantity);
    }
  };
  
  // 切换选项
  const toggleOption = (option: keyof typeof options) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };
  
  // 可用矿机列表（模拟数据）
  const miners = [
    {
      id: 1,
      name: 'Antminer S21 Pro',
      hashrate: '230 TH/s',
      image: '/assets/miner-s21.jpg',
      basePrice: 9999
    },
    {
      id: 2,
      name: 'Whatsminer M50S',
      hashrate: '126 TH/s',
      image: '/assets/miner-m50s.jpg',
      basePrice: 5499
    },
    {
      id: 3,
      name: 'Avalon A1366',
      hashrate: '72 TH/s',
      image: '/assets/miner-a1366.jpg',
      basePrice: 2999
    }
  ];
  
  // 电源选项（模拟数据）
  const powerSupplyOptions = [
    { value: 'standard', label: t('asicShop.powerSupply.standard', '标准电源'), price: 0 },
    { value: 'premium', label: t('asicShop.powerSupply.premium', '高级电源 (+5%)'), price: 0.05 },
    { value: 'redundant', label: t('asicShop.powerSupply.redundant', '冗余电源 (+10%)'), price: 0.1 }
  ];
  
  // 固件选项（模拟数据）
  const firmwareOptions = [
    { value: 'standard', label: t('asicShop.firmware.standard', '标准固件'), price: 0 },
    { value: 'advanced', label: t('asicShop.firmware.advanced', '高级固件 (+$99)'), price: 99 },
    { value: 'expert', label: t('asicShop.firmware.expert', '专家固件 (+$199)'), price: 199 }
  ];
  
  // 获取选中的矿机
  const selectedMinerData = miners.find(m => m.id === selectedMiner) || null;
  
  // 计算价格
  const calculatePrice = () => {
    if (!selectedMinerData) return 0;
    
    let total = selectedMinerData.basePrice;
    
    // 添加电源费用
    const selectedPowerSupply = powerSupplyOptions.find(p => p.value === powerSupply);
    if (selectedPowerSupply) {
      total += selectedMinerData.basePrice * selectedPowerSupply.price;
    }
    
    // 添加固件费用
    const selectedFirmware = firmwareOptions.find(f => f.value === firmware);
    if (selectedFirmware) {
      total += selectedFirmware.price;
    }
    
    // 添加附加选项费用
    if (options.overclocking) total += 299;
    if (options.customBranding) total += 199;
    if (options.extendedWarranty) total += selectedMinerData.basePrice * 0.12; // 12% of base price
    if (options.prioritySupport) total += 399;
    
    // 乘以数量
    total *= quantity;
    
    return total;
  };
  
  // 计算总价
  const totalPrice = calculatePrice();
  
  return (
    <PageContainer>
      <BackLink to="/products/asic-shop/products">
        {t('common.backToProducts', '返回产品列表')}
      </BackLink>
      
      <Title>{t('asicShop.customization.title', '定制您的ASIC矿机')}</Title>
      <Description>
        {t('asicShop.customization.description', '根据您的需求定制矿机配置和服务，优化您的挖矿体验和回报。')}
      </Description>
      
      <ContentContainer>
        {/* 左侧定制选项 */}
        <CustomizationSection>
          {/* 矿机选择 */}
          <OptionGroup>
            <OptionGroupTitle>{t('asicShop.customization.chooseMiner', '选择矿机型号')}</OptionGroupTitle>
            <OptionGroupDescription>
              {t('asicShop.customization.chooseMinerDesc', '选择您想要定制的基础矿机型号。不同型号具有不同的算力和基础功能。')}
            </OptionGroupDescription>
            
            <MinerSelection>
              {miners.map(miner => (
                <MinerOption 
                  key={miner.id}
                  selected={selectedMiner === miner.id}
                  onClick={() => setSelectedMiner(miner.id)}
                >
                  <MinerImage style={{ background: `url(${miner.image || '/assets/placeholder-miner.jpg'}) center/cover no-repeat` }} />
                  <MinerName>{miner.name}</MinerName>
                  <MinerHashrate>{miner.hashrate}</MinerHashrate>
                </MinerOption>
              ))}
            </MinerSelection>
          </OptionGroup>
          
          {/* 基本配置 */}
          <OptionGroup>
            <OptionGroupTitle>{t('asicShop.customization.basicConfig', '基本配置')}</OptionGroupTitle>
            <OptionGroupDescription>
              {t('asicShop.customization.basicConfigDesc', '选择矿机的基本配置，包括数量、电源类型和固件版本。')}
            </OptionGroupDescription>
            
            <SelectionOptions>
              <OptionRow>
                <OptionLabel htmlFor="quantity-input">{t('asicShop.customization.quantity', '数量')}</OptionLabel>
                <QuantityInput>
                  <QuantityButton 
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    aria-label={t('asicShop.customization.decreaseQuantity', '减少数量')}
                  >
                    −
                  </QuantityButton>
                  <QuantityDisplay 
                    id="quantity-input"
                    type="text" 
                    value={quantity} 
                    readOnly 
                    aria-label={t('asicShop.customization.quantity', '数量')}
                  />
                  <QuantityButton 
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= 100}
                    aria-label={t('asicShop.customization.increaseQuantity', '增加数量')}
                  >
                    +
                  </QuantityButton>
                </QuantityInput>
              </OptionRow>
              
              <OptionRow>
                <OptionLabel htmlFor="power-supply-select">{t('asicShop.customization.powerSupply', '电源类型')}</OptionLabel>
                <SelectInput 
                  id="power-supply-select"
                  value={powerSupply} 
                  onChange={(e) => setPowerSupply(e.target.value)}
                  aria-label={t('asicShop.customization.powerSupply', '电源类型')}
                  title={t('asicShop.customization.powerSupply', '电源类型')}
                >
                  {powerSupplyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </OptionRow>
              
              <OptionRow>
                <OptionLabel htmlFor="firmware-select">{t('asicShop.customization.firmware', '固件版本')}</OptionLabel>
                <SelectInput 
                  id="firmware-select"
                  value={firmware} 
                  onChange={(e) => setFirmware(e.target.value)}
                  aria-label={t('asicShop.customization.firmware', '固件版本')}
                  title={t('asicShop.customization.firmware', '固件版本')}
                >
                  {firmwareOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </OptionRow>
            </SelectionOptions>
          </OptionGroup>
          
          {/* 高级选项 */}
          <OptionGroup>
            <OptionGroupTitle>{t('asicShop.customization.advancedOptions', '高级选项')}</OptionGroupTitle>
            <OptionGroupDescription>
              {t('asicShop.customization.advancedOptionsDesc', '选择额外的服务和功能来优化您的矿机性能和挖矿体验。')}
            </OptionGroupDescription>
            
            <SelectionOptions>
              <CheckboxContainer 
                checked={options.overclocking}
                onClick={() => toggleOption('overclocking')}
              >
                <Checkbox checked={options.overclocking} />
                <CheckboxLabel>
                  <CheckboxTitle>
                    {t('asicShop.customization.overclocking', '超频优化')} (+$299)
                  </CheckboxTitle>
                  <CheckboxDescription>
                    {t('asicShop.customization.overclockingDesc', '专业团队优化矿机超频设置，提升算力5-15%')}
                  </CheckboxDescription>
                </CheckboxLabel>
              </CheckboxContainer>
              
              <CheckboxContainer 
                checked={options.customBranding}
                onClick={() => toggleOption('customBranding')}
              >
                <Checkbox checked={options.customBranding} />
                <CheckboxLabel>
                  <CheckboxTitle>
                    {t('asicShop.customization.customBranding', '定制品牌')} (+$199)
                  </CheckboxTitle>
                  <CheckboxDescription>
                    {t('asicShop.customization.customBrandingDesc', '为您的矿机添加定制logo和颜色方案')}
                  </CheckboxDescription>
                </CheckboxLabel>
              </CheckboxContainer>
              
              <CheckboxContainer 
                checked={options.extendedWarranty}
                onClick={() => toggleOption('extendedWarranty')}
              >
                <Checkbox checked={options.extendedWarranty} />
                <CheckboxLabel>
                  <CheckboxTitle>
                    {t('asicShop.customization.extendedWarranty', '延长保修')} (+12%)
                  </CheckboxTitle>
                  <CheckboxDescription>
                    {t('asicShop.customization.extendedWarrantyDesc', '将标准保修期从12个月延长至36个月')}
                  </CheckboxDescription>
                </CheckboxLabel>
              </CheckboxContainer>
              
              <CheckboxContainer 
                checked={options.prioritySupport}
                onClick={() => toggleOption('prioritySupport')}
              >
                <Checkbox checked={options.prioritySupport} />
                <CheckboxLabel>
                  <CheckboxTitle>
                    {t('asicShop.customization.prioritySupport', '优先支持')} (+$399)
                  </CheckboxTitle>
                  <CheckboxDescription>
                    {t('asicShop.customization.prioritySupportDesc', '获得优先技术支持和24小时响应承诺')}
                  </CheckboxDescription>
                </CheckboxLabel>
              </CheckboxContainer>
            </SelectionOptions>
          </OptionGroup>
        </CustomizationSection>
        
        {/* 右侧订单摘要 */}
        <SummarySection>
          <SummaryTitle>{t('asicShop.customization.orderSummary', '订单摘要')}</SummaryTitle>
          
          <SummaryItemContainer>
            <SummaryItem>
              <SummaryItemLabel>{t('asicShop.customization.selectedModel', '选中型号')}</SummaryItemLabel>
              <SummaryItemValue>{selectedMinerData?.name || '未选择'}</SummaryItemValue>
            </SummaryItem>
            
            <SummaryItem>
              <SummaryItemLabel>{t('asicShop.customization.basePrice', '基础价格')}</SummaryItemLabel>
              <SummaryItemValue>{selectedMinerData ? `$${selectedMinerData.basePrice.toLocaleString()}` : '-'}</SummaryItemValue>
            </SummaryItem>
            
            <SummaryItem>
              <SummaryItemLabel>{t('asicShop.customization.quantity', '数量')}</SummaryItemLabel>
              <SummaryItemValue>{quantity}</SummaryItemValue>
            </SummaryItem>
            
            <SummaryItem>
              <SummaryItemLabel>{t('asicShop.customization.powerSupply', '电源类型')}</SummaryItemLabel>
              <SummaryItemValue>
                {powerSupplyOptions.find(p => p.value === powerSupply)?.label || '-'}
              </SummaryItemValue>
            </SummaryItem>
            
            <SummaryItem>
              <SummaryItemLabel>{t('asicShop.customization.firmware', '固件版本')}</SummaryItemLabel>
              <SummaryItemValue>
                {firmwareOptions.find(f => f.value === firmware)?.label || '-'}
              </SummaryItemValue>
            </SummaryItem>
            
            {options.overclocking && (
              <SummaryItem>
                <SummaryItemLabel>{t('asicShop.customization.overclocking', '超频优化')}</SummaryItemLabel>
                <SummaryItemValue>$299</SummaryItemValue>
              </SummaryItem>
            )}
            
            {options.customBranding && (
              <SummaryItem>
                <SummaryItemLabel>{t('asicShop.customization.customBranding', '定制品牌')}</SummaryItemLabel>
                <SummaryItemValue>$199</SummaryItemValue>
              </SummaryItem>
            )}
            
            {options.extendedWarranty && (
              <SummaryItem>
                <SummaryItemLabel>{t('asicShop.customization.extendedWarranty', '延长保修')}</SummaryItemLabel>
                <SummaryItemValue>
                  {selectedMinerData ? `$${(selectedMinerData.basePrice * 0.12).toLocaleString()}` : '-'}
                </SummaryItemValue>
              </SummaryItem>
            )}
            
            {options.prioritySupport && (
              <SummaryItem>
                <SummaryItemLabel>{t('asicShop.customization.prioritySupport', '优先支持')}</SummaryItemLabel>
                <SummaryItemValue>$399</SummaryItemValue>
              </SummaryItem>
            )}
          </SummaryItemContainer>
          
          <TotalContainer>
            <TotalLabel>{t('asicShop.customization.totalPrice', '总价')}</TotalLabel>
            <TotalValue>${totalPrice.toLocaleString()} USDT</TotalValue>
          </TotalContainer>
          
          <div>
            <DeliveryOption>
              <span style={{ fontSize: '0.9rem', color: '$textSecondary' }}>
                {t('asicShop.customization.estimatedDelivery', '预计发货时间')}: 7-14 {t('asicShop.customization.days', '天')}
              </span>
            </DeliveryOption>
            
            <Button variant="primary" fullWidth>
              {t('asicShop.customization.placeOrder', '提交订单')}
            </Button>
          </div>
        </SummarySection>
      </ContentContainer>
    </PageContainer>
  );
};

export default AsicShopCustomization; 